import React from 'react';

const Invoice = ({ order }) => {
  if (!order) return null;

  const {
    id,
    created_at,
    userEmail,
    items = [],
    total,
    subtotal,
    taxAmount,
    taxBreakdown = [],
    shippingAddress
  } = order;

  return (
    <div className="bg-white text-gray-800 p-8 max-w-4xl mx-auto border border-gray-200 shadow-lg rounded-xl font-sans" id="printable-invoice">
      {/* Header */}
      <div className="flex justify-between items-start mb-12 border-b border-gray-100 pb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 mb-2">INVOICE</h1>
          <p className="text-gray-500 font-medium text-sm">#ORDER-{id || 'PENDING'}</p>
        </div>
        <div className="text-right">
          <h2 className="text-2xl font-bold text-raya-accent">Raya Elegance</h2>
          <p className="text-sm text-gray-500 mt-1">Summerville, SC</p>
          <p className="text-sm text-gray-500">info@raya-elegance.com</p>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-12 mb-12">
        <div>
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Bill To</h3>
          <p className="font-bold text-gray-900 text-lg">{shippingAddress?.firstName} {shippingAddress?.lastName}</p>
          <p className="text-gray-600 leading-relaxed mt-1">
            {shippingAddress?.address}<br />
            {shippingAddress?.city}, {shippingAddress?.state} {shippingAddress?.zip}<br />
            {shippingAddress?.county && `County: ${shippingAddress?.county}`}
          </p>
          <p className="text-gray-600 mt-2">{userEmail}</p>
        </div>
        <div className="text-right">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Order Details</h3>
          <p className="text-gray-600"><span className="font-semibold text-gray-900">Date:</span> {new Date(created_at || Date.now()).toLocaleDateString()}</p>
          <p className="text-gray-600 mt-1"><span className="font-semibold text-gray-900">Status:</span> Paid</p>
          <p className="text-gray-600 mt-1"><span className="font-semibold text-gray-900">Payment:</span> Credit Card (via Stripe)</p>
        </div>
      </div>

      {/* Table */}
      <div className="mb-12 overflow-hidden rounded-lg border border-gray-100">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Item Description</th>
              <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Qty</th>
              <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Unit Price</th>
              <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item, idx) => (
              <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-6 py-5">
                  <div className="font-bold text-gray-900">{item.name || 'Product'}</div>
                  <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{item.category || 'General'}</div>
                </td>
                <td className="px-6 py-5 text-center font-medium">{item.quantity}</td>
                <td className="px-6 py-5 text-right text-gray-600">${parseFloat(item.price).toFixed(2)}</td>
                <td className="px-6 py-5 text-right font-bold text-gray-900">${(item.price * item.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end pt-8 border-t border-gray-100">
        <div className="w-full max-w-xs space-y-3">
          <div className="flex justify-between text-gray-600">
            <span className="font-medium text-sm">Subtotal</span>
            <span className="font-bold font-mono text-gray-900">${parseFloat(subtotal || total - (taxAmount || 0)).toFixed(2)}</span>
          </div>
          
          <div className="bg-gray-50/80 rounded-lg p-4 space-y-2 mt-4">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Tax Breakdown</h4>
            {taxBreakdown.map((tax, idx) => (
              tax.amount > 0 && (
                <div key={idx} className="flex justify-between text-xs text-gray-600">
                  <span className="font-medium">{tax.name} ({tax.rate})</span>
                  <span className="font-bold text-gray-900">${(tax.amount / 100).toFixed(2)}</span>
                </div>
              )
            ))}
            <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between font-bold text-gray-900 text-sm">
              <span>Total Tax</span>
              <span>${parseFloat(taxAmount || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="flex justify-between items-center bg-gray-900 text-white rounded-lg p-5 mt-6 shadow-xl">
            <span className="text-sm font-black uppercase tracking-widest">Grand Total</span>
            <span className="text-2xl font-black font-mono">${parseFloat(total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-20 pt-8 border-t border-gray-100 text-center">
        <p className="text-gray-400 text-xs font-medium tracking-wide">
          Thank you for choosing Raya Elegance. Prepared in Summerville, SC.
        </p>
        <p className="text-[10px] text-gray-300 mt-2 uppercase tracking-[0.2em]">
          All applicable sales and hospitality taxes included as per SC regulation.
        </p>
      </div>
      
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-invoice, #printable-invoice * { visibility: visible; }
          #printable-invoice { position: absolute; left: 0; top: 0; width: 100%; border: none; shadow: none; }
          .no-print { display: none; }
        }
      `}</style>
    </div>
  );
};

export default Invoice;
